import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';

import { GetPurchaseDetailsQuery as GetPurchaseDetails } from '__generated__/graphql';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditPurchaseFromBackendMutation from './EditPurchaseFromBackendMutation';
import GetOrderQuery from './GetOrderQuery';
import GetParticipantOrdersQuery from './GetParticipantOrdersQuery';
import GetPurchaseDetailsQuery from './GetPurchaseDetailsQuery';
import PurchaseInfo from './PurchaseInfo';
import UI from '../../../../common/UI';
import useCurrencyInput from '../../../../common/useCurrencyInput';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

type Purchase = GetPurchaseDetails['project']['purchase'];

interface ApplyCreditFormProps {
  purchase: Purchase;
  eventId: string;
  refetchQueries: DocumentNode[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ApplyCreditForm = ({
  purchase, eventId, onSuccess, onCancel, refetchQueries = [GetParticipantOrdersQuery, GetOrderQuery],
}: ApplyCreditFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const notifier = useNotifier();
  const project = useProject();

  const { participant } = purchase.order;

  const maxRevenue = Math.min(
    Math.max(purchase.amount, purchase.revenue),
    purchase.revenue + participant.balance - purchase.balance,
  );

  const getValues = () => ({
    revenue: maxRevenue,
  });

  const form = useForm({
    values: getValues(),
  });

  const [editPurchase, { error, loading }] = useMutation(
    EditPurchaseFromBackendMutation,
    {
      variables: {
        input: {
          id: purchase.id,
          revenue: form.values.revenue,
          amount: purchase.amount,
          refund: false,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onSuccess?.();

        notifier.success(t('notifications.purchase_edited'));
      },
    },
  );

  const refundSchema = useMemo(() => (
    yup.object().shape({
      revenue: (yup as any).number().minAmount(purchase.revenue).maxAmount(maxRevenue).required(),
    })
  ), [purchase, maxRevenue]);

  const { valid, errors } = useValidation({
    schema: refundSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const currencyInput = useCurrencyInput();

  const revenueInputRef = useRef<HTMLInputElement>();

  const setMinAmount = () => {
    form.set('revenue', purchase.revenue);
    revenueInputRef.current.value = currencyInput.value(purchase.revenue);
  };

  const setMaxAmount = () => {
    form.set('revenue', maxRevenue);
    revenueInputRef.current.value = currencyInput.value(maxRevenue);
  };

  return (
    <UI.Form onSubmit={() => editPurchase()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('apply_credit')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('apply_credit_description')}
          </UI.Paragraph>
        </UI.Div>

        <PurchaseInfo purchase={purchase} />

        <UI.ServerErrors error={error} />

        {purchase.order.participant.id !== participant.id && (
          <UI.Warning>
            <Trans
              i18nKey={`${purchase.ticket ? 'ticket' : 'product'}_purchased_by`}
              values={{
                name: purchase.order.participant.full_name,
              }}
            >
              <UI.Link
                to={`/${project.id}/participants/${purchase.order.participant.id}?events[0]=${eventId}`}
                sc={{ strong: true }}
              />
            </Trans>
          </UI.Warning>
        )}

        <UI.InputGroup sc={{ valid: !errors.revenue, touched: form.touched.revenue }}>
          <UI.InputLabel htmlFor="PurchaseRevenue">
            {t('paid_amount')}
          </UI.InputLabel>
          <UI.CurrencyInput
            {...form.text('revenue', {
              inputHandler: {
                parse: currencyInput.parse,
                format: currencyInput.value,
              },
            })}
            ref={revenueInputRef}
            id="PurchaseRevenue"
          />
          <UI.Div sc={{ small: true, mt: 1 }}>
            <UI.Delimit>
              <UI.A
                onClick={() => setMinAmount()}
                sc={{ secondary: true, muted: form.values.revenue === purchase.revenue }}
              >
                {`${t('min')}: ${formatCurrency(purchase.revenue)}`}
              </UI.A>
              <UI.A
                onClick={() => setMaxAmount()}
                sc={{ secondary: true, muted: form.values.revenue === maxRevenue }}
              >
                {`${t('max')}: ${formatCurrency(maxRevenue)}`}
              </UI.A>
            </UI.Delimit>
          </UI.Div>
          <UI.ErrorMessages
            attribute={t('paid_amount')}
            errors={errors.revenue}
          />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('confirm')}
          enabled
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseApplyCreditModalProps {
  purchaseId: string;
  eventId: string;
  open?: boolean;
  refetchQueries?: DocumentNode[];
}

const useApplyCreditModal = ({
  purchaseId, eventId, open = false, refetchQueries,
}: UseApplyCreditModalProps) => {
  const project = useProject();

  const [openModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      closeModal();
    };

    const { data } = useQuery(
      GetPurchaseDetailsQuery,
      {
        variables: {
          project_id: project.id,
          purchase_id: purchaseId,
        },
        fetchPolicy: 'no-cache',
      },
    );

    const purchase = data?.project.purchase;

    return (
      <UI.Modal close={closeModal}>
        {!purchase && <UI.PageLoader />}

        {purchase && (
          <ApplyCreditForm
            purchase={purchase}
            eventId={eventId}
            onSuccess={handleSuccess}
            onCancel={closeModal}
            refetchQueries={refetchQueries}
          />
        )}
      </UI.Modal>
    );
  }, [purchaseId, eventId]);

  useEffect(() => {
    if (open) {
      openModal();
    } else {
      closeModal();
    }
  }, [open]);

  return [openModal, closeModal];
};

export default useApplyCreditModal;
